import { useState, useCallback } from 'react';
import {
  fetchScheduledGames,
  createScheduledGame as createScheduledGameService,
  cancelScheduledGame,
  updateScheduledGameStatus as updateScheduledGameStatusService,
  clearScheduledGamePlayerFlags,
  updateScheduledGameOrder,
} from '../services/scheduledGamesService';
import { buildLocalScheduledGame } from '../utils/session/buildLocalScheduledGame';
import { OFFLINE_COMMAND_TYPES, shouldHoldRemoteUpcoming, shouldRunLocalHostPath } from '../utils/session/offlineQueueConstants';

/**
 * Scheduled games state and CRUD for a session.
 * @param {Object} params
 * @param {string} params.sessionId
 * @param {string} [params.clubId]
 * @param {string} [params.currentUserId]
 * @param {Function} [params.addToast] - optional toast callback for conflict/error messages
 * @returns {{ scheduledGames, setScheduledGames, loadScheduledGames, createScheduledGame, removeScheduledGame, updateScheduledGameStatus, reorderScheduledGames, showAddScheduledGameModal, setShowAddScheduledGameModal }}
 */
export default function useScheduledGames({
  sessionId,
  clubId,
  currentUserId,
  addToast = null,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
  getPlayersById = null,
}) {
  const [scheduledGames, setScheduledGames] = useState([]);
  const [showAddScheduledGameModal, setShowAddScheduledGameModal] = useState(false);

  const loadScheduledGames = useCallback(async () => {
    if (!sessionId) return;
    if (shouldHoldRemoteUpcoming(offlineQueue, session)) return;
    try {
      const games = await fetchScheduledGames(sessionId);
      setScheduledGames(Array.isArray(games) ? games : []);
    } catch (error) {
      console.error('[SCHEDULED GAMES] Error loading scheduled games:', error);
      setScheduledGames([]);
    }
  }, [sessionId, offlineQueue, session]);

  const createScheduledGame = useCallback(async (team1, team2) => {
    if (shouldRunLocalHostPath(offlineQueue, session)) {
      const localGame = buildLocalScheduledGame({
        sessionId,
        clubId,
        team1,
        team2,
        orderIndex: scheduledGames.length,
        createdBy: currentUserId,
        playersById: getPlayersById?.() || {},
      });
      const nextGames = [...scheduledGames, localGame].sort(
        (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
      );
      setScheduledGames(nextGames);
      await offlineQueue.enqueue(
        {
          type: OFFLINE_COMMAND_TYPES.SCHEDULED_GAME,
          action: 'create',
          scheduledGameId: localGame.id,
          team1,
          team2,
          orderIndex: localGame.order_index,
          createdBy: currentUserId,
        },
        { ...(getSnapshot?.() || {}), scheduledGames: nextGames }
      );
      return localGame;
    }

    const newGame = await createScheduledGameService(
      sessionId,
      clubId,
      team1,
      team2,
      currentUserId
    );
    setScheduledGames(prev => [...prev, newGame].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)));
    return newGame;
  }, [sessionId, clubId, currentUserId, offlineQueue, session, scheduledGames, getSnapshot, getPlayersById]);

  const removeScheduledGame = useCallback(async (scheduledGameId, tabInstanceId = null) => {
    if (shouldRunLocalHostPath(offlineQueue, session)) {
      let nextGames = [];
      setScheduledGames(prev => {
        const filtered = prev.filter(g => g.id !== scheduledGameId);
        nextGames = filtered.map((g, i) => ({ ...g, order_index: i }));
        return nextGames;
      });
      await offlineQueue.enqueue(
        {
          type: OFFLINE_COMMAND_TYPES.SCHEDULED_GAME,
          action: 'cancel',
          scheduledGameId,
        },
        { ...(getSnapshot?.() || {}), scheduledGames: nextGames }
      );
      return;
    }

    await cancelScheduledGame(scheduledGameId, tabInstanceId);
    setScheduledGames(prev => {
      const filtered = prev.filter(g => g.id !== scheduledGameId);
      return filtered.map((g, i) => ({ ...g, order_index: i }));
    });
  }, [offlineQueue, session, getSnapshot]);

  /**
   * Update a scheduled game's status (e.g. pending → on_court → completed).
   * For on_court transitions, uses an optimistic lock — returns { conflict: true }
   * and shows a toast if another host already promoted the same game.
   * Completed games are removed from local state (hidden from sidebar).
   */
  const updateScheduledGameStatus = useCallback(async (scheduledGameId, status) => {
    if (!shouldRunLocalHostPath(offlineQueue, session)) {
      const result = await updateScheduledGameStatusService(scheduledGameId, status);

      if (result?.conflict) {
        if (typeof addToast === 'function') {
          addToast('This scheduled game was already placed by another host', 'warning');
        }
        await loadScheduledGames();
        return { conflict: true };
      }

      if (sessionId && (status === 'placed' || status === 'completed')) {
        await clearScheduledGamePlayerFlags(sessionId, scheduledGameId);
      }
    }
    setScheduledGames(prev => {
      if (status === 'completed') {
        return prev.filter(g => g.id !== scheduledGameId);
      }
      return prev.map(g => g.id === scheduledGameId ? { ...g, status } : g);
    });
    return { success: true };
  }, [sessionId, addToast, loadScheduledGames, offlineQueue, session]);

  const reorderScheduledGames = useCallback(async (dragIndex, dropIndex) => {
    const reordered = [...scheduledGames];
    const [removed] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, removed);
    const updated = reordered.map((game, index) => ({
      ...game,
      order_index: index
    }));
    setScheduledGames(updated);
    try {
      if (!shouldRunLocalHostPath(offlineQueue, session)) {
        await updateScheduledGameOrder(sessionId, updated);
      }
    } catch (error) {
      console.error('Error reordering scheduled games:', error);
      setScheduledGames(prev => prev);
      throw error;
    }
  }, [sessionId, scheduledGames, offlineQueue, session]);

  return {
    scheduledGames,
    setScheduledGames,
    loadScheduledGames,
    createScheduledGame,
    removeScheduledGame,
    updateScheduledGameStatus,
    reorderScheduledGames,
    showAddScheduledGameModal,
    setShowAddScheduledGameModal,
  };
}
